import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import Table from "../../../../components/TableV2";
import TitleButtons from "../../../../components/TitleButtons";
import { generatePdfReport } from "../../../../utils/pdf/generatePdfReport";
import numeroParaExtenso from "../../../../utils/numeroPorExtenso";
import { useAuth } from "../../../../context/auth-context";

/** ---------------------- Tipos ---------------------- */

type EntradasConfig = {
  areaSlug: string;
  estoqueSlug: string;
  key: string;
  title: string;
  reportTitle: string;
  fileNamePrefix: string;
};

type ModuloAtual = {
  id: number;
  codigo: string;
  label: string;
};

export type EntradaItemAPI = {
  id: number | string;
  movimentacao_id: number | string;
  produto_id: number | string;
  estoque_id?: number | string | null;
  quantidade: number | string;
  custo_unitario: string | number | null;
  moeda?: string | null;
  produto: {
    id: number | string;
    codigo: string;
    nome: string;
    ncm?: string | null;
    classificacao?: string | null;
    posicao: string | null;
    peso?: string | number | null;
    dimensao?: string | null;
    ocultar?: boolean | null;
  };
};

export type EntradaMovAPI = {
  movimentacao: {
    id: number | string;
    empresa_id?: number | string | null;
    estoque_id?: number | string | null;
    tipo_movimentacao_id?: number | string | null;
    codigo_movimentacao?: string | null;
    tipo?: string | null;
    responsavel_id: number | string | null;
    responsavel_nome: string | null;

    os_numero?: string | null;
    coletor_nome?: string | null;
    embarcacao?: string | null;
    cliente_nome?: string | null;
    empresa_cliente?: string | null;
    proposta_venda?: string | null;

    custo_total?: string | number | null;
    motivo?: string | null;
    criado_em: string;

    tipo_movimentacao?: {
      codigo?: string | null;
      nome?: string | null;
      natureza?: string | null;
      sinal?: number | string | null;
    };

    estoque?: {
      id?: number | string | null;
      codigo?: string | null;
      nome?: string | null;
      modulo_id?: number | string | null;
    };

    modulo?: {
      id?: number | string | null;
      codigo?: string | null;
      label?: string | null;
    };
  };
  itens: EntradaItemAPI[];
};

type RespAPI = {
  ok: boolean;
  fonte?: "cache" | "banco";
  estoque?: {
    id?: number | string;
    codigo?: string;
    nome?: string;
    modulo_id?: number | string;
  };
  modulo?: {
    id?: number | string;
    codigo?: string;
    label?: string;
  };
  meta: {
    filtros: {
      date_from: string | null;
      date_to: string | null;
      order: "ASC" | "DESC" | "asc" | "desc";
      limit: number;
      offset: number;
    };
    count: number;
    total?: number;
    has_more?: boolean;
  };
  data: EntradaMovAPI[];
};

export type EntradaRowUI = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string
];

export type EntradasQuery = Partial<{
  date_from: string;
  date_to: string;
  order: "asc" | "desc";
  limit: number;
  offset: number;
}>;

/** ---------------------- Constantes ---------------------- */

const URL_ENTRADAS =
  "https://api.logvi-havox.com/template/estoque-administrativo/get-entradas";

const POLL_FALLBACK_MS = 15000;
const ERROR_BACKOFF_MS = 5000;

const ACCESS_TOKEN_KEY = "access_token";

/** ---------------------- Config / sessão ---------------------- */

const ESTOQUE_SLUG_TO_MODULO_CODIGO: Record<string, string> = {
  "estoque-p-servico-e-comercial": "suprimentos-estoque",
  "estoque-p-servico-campos": "suprimentos-estoque-campos",
  "estoque-p-uso-coorporativo": "rh-estoque",
  "estoque-p-tecnicos-e-laboratorio": "tec-estoque",
  "materiais-p-venda": "suprimentos-estoque",
  "coordenacao-rastreio-de-materiais": "coordenacao",
};

function normalizeSlug(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^\/+|\/+$/g, "")
    .toLowerCase();
}

function normalizeComparable(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/_/g, "-")
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

function slugToTitle(slug: string): string {
  return String(slug || "")
    .split("-")
    .filter(Boolean)
    .map((part) => {
      if (part === "p") return "p/";
      if (part === "e") return "e";
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(" ")
    .replace(/\s+p\/\s+/g, " p/ ");
}

function getConfigByParams(
  areaSlug?: string,
  estoqueSlug?: string
): EntradasConfig {
  const area = normalizeSlug(areaSlug) || "suprimentos";
  const estoque = normalizeSlug(estoqueSlug) || "estoque";

  const estoqueTitle = slugToTitle(estoque);

  return {
    areaSlug: area,
    estoqueSlug: estoque,
    key: `${area}-${estoque}`,
    title: `${slugToTitle(area)} - Entradas - ${estoqueTitle}`,
    reportTitle: `Relatório de Entradas Estoque - ${estoqueTitle}`,
    fileNamePrefix: `${area}-${estoque}-Entradas`,
  };
}

function findModuloAtualFromSessao(
  sessao: any,
  config: EntradasConfig
): ModuloAtual | null {
  const areaSlug = normalizeComparable(config.areaSlug);
  const estoqueSlug = normalizeComparable(config.estoqueSlug);

  const expectedModuloCodigo =
    ESTOQUE_SLUG_TO_MODULO_CODIGO[estoqueSlug] || estoqueSlug;

  const enderecos = Array.isArray(sessao?.enderecos_com_modulos)
    ? sessao.enderecos_com_modulos
    : [];

  const enderecoSelecionadoId =
    sessao?.endereco_selecionado_id !== undefined &&
    sessao?.endereco_selecionado_id !== null
      ? String(sessao.endereco_selecionado_id)
      : null;

  const enderecosOrdenados = [
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) === enderecoSelecionadoId : false
    ),
    ...enderecos.filter((e: any) =>
      enderecoSelecionadoId ? String(e?.id) !== enderecoSelecionadoId : true
    ),
  ];

  for (const endereco of enderecosOrdenados) {
    const departamentos = Array.isArray(endereco?.departamentos_acessiveis)
      ? endereco.departamentos_acessiveis
      : [];

    for (const dep of departamentos) {
      const depSlug = normalizeComparable(dep?.slug ?? dep?.routeKey);

      if (areaSlug && depSlug && depSlug !== areaSlug) {
        continue;
      }

      const modulos = Array.isArray(dep?.modulos) ? dep.modulos : [];

      const foundByCodigo = modulos.find((mod: any) => {
        const modCodigo = normalizeComparable(mod?.codigo);
        return modCodigo === normalizeComparable(expectedModuloCodigo);
      });

      const found =
        foundByCodigo ||
        modulos.find((mod: any) => {
          const modCodigo = normalizeComparable(mod?.codigo);
          const modLabel = normalizeComparable(mod?.label);

          return (
            modCodigo === estoqueSlug ||
            modLabel === estoqueSlug ||
            modCodigo.includes(estoqueSlug) ||
            estoqueSlug.includes(modCodigo)
          );
        });

      if (found?.id !== undefined && found?.id !== null) {
        const id = Number(found.id);

        if (Number.isInteger(id) && id > 0) {
          return {
            id,
            codigo: String(found.codigo || ""),
            label: String(found.label || ""),
          };
        }
      }
    }
  }

  return null;
}

/** ---------------------- Utils ---------------------- */

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

const jitter = (ms: number) => ms + Math.floor(Math.random() * 400);

function getAccessToken(): string | null {
  try {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

function buildAuthHeaders(includeContentType = false): Record<string, string> {
  const token = getAccessToken();

  const headers: Record<string, string> = {
    Accept: "application/json",
  };

  if (includeContentType) {
    headers["Content-Type"] = "application/json";
  }

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
}

function toBRT(iso: string): { data: string; hora: string } {
  if (!iso) return { data: "-", hora: "-" };

  try {
    const d = new Date(iso);

    if (Number.isNaN(d.getTime())) {
      return {
        data: iso,
        hora: "",
      };
    }

    const data = d.toLocaleDateString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });

    const hora = d.toLocaleTimeString("pt-BR", {
      timeZone: "America/Sao_Paulo",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    return { data, hora };
  } catch {
    return { data: iso, hora: "" };
  }
}

function formatMoney(valor: number, moeda?: string | null): string {
  const currency = String(moeda || "BRL").toUpperCase();

  const symbol =
    currency === "USD"
      ? "$"
      : currency === "EUR"
      ? "€"
      : currency === "BRL"
      ? "R$"
      : currency;

  return `${symbol} ${valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function parseCurrency(value: unknown): number {
  const raw = String(value ?? "")
    .replace(/[^\d,.-]/g, "")
    .trim();

  if (!raw) return 0;

  const lastComma = raw.lastIndexOf(",");
  const lastDot = raw.lastIndexOf(".");

  let normalized = raw;

  if (lastComma > lastDot) {
    normalized = raw.replace(/\./g, "").replace(",", ".");
  } else if (lastDot > lastComma) {
    normalized = raw.replace(/,/g, "");
  }

  const n = Number(normalized);

  return Number.isFinite(n) ? n : 0;
}

function toNumber(value: unknown): number {
  const n =
    typeof value === "number"
      ? value
      : Number(String(value ?? "0").replace(",", "."));

  return Number.isFinite(n) ? n : 0;
}

function flattenToRows(movs: EntradaMovAPI[]): EntradaRowUI[] {
  const rows: EntradaRowUI[] = [];

  for (const mov of movs || []) {
    const { data, hora } = toBRT(mov.movimentacao?.criado_em || "");
    const responsavel = mov.movimentacao?.responsavel_nome || "-";

    for (const item of mov.itens || []) {
      const quantidade = toNumber(item.quantidade);
      const custo = toNumber(item.custo_unitario);
      const moeda = item.moeda || "BRL";

      rows.push([
        responsavel,
        String(item.produto?.codigo ?? "").toUpperCase(),
        String(item.produto?.nome ?? "").toUpperCase(),
        formatMoney(custo, moeda),
        String(Number.isFinite(quantidade) ? quantidade : 0),
        item.produto?.posicao ?? "-",
        moeda,
        data,
        hora,
      ]);
    }
  }

  return rows;
}

function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

/** ---------------------- Fetchers ---------------------- */

async function fetchEntradasOnce(
  moduloAtual: ModuloAtual | null,
  query?: EntradasQuery
): Promise<{
  rows: EntradaRowUI[];
  meta: RespAPI["meta"] | null;
  resp: RespAPI | null;
}> {
  if (!moduloAtual?.id) {
    throw new Error("Módulo de estoque não identificado para esta rota");
  }

  const payload = {
    modulo_id: moduloAtual.id,
    order: query?.order ?? "desc",
    limit: query?.limit ?? 100,
    offset: query?.offset ?? 0,
    ...(query?.date_from ? { date_from: query.date_from } : {}),
    ...(query?.date_to ? { date_to: query.date_to } : {}),
  };

  const res = await fetch(URL_ENTRADAS, {
    method: "POST",
    headers: buildAuthHeaders(true),
    cache: "no-store",
    body: JSON.stringify(payload),
  });

  const parsedJson = (await res.json().catch(() => null)) as RespAPI | null;

  if (!res.ok || parsedJson?.ok === false) {
    const erro =
      (parsedJson as any)?.erro ||
      (Array.isArray((parsedJson as any)?.erros) &&
        (parsedJson as any).erros.join(" | ")) ||
      `HTTP ${res.status}`;

    throw new Error(erro);
  }

  const dataRaw: EntradaMovAPI[] =
    parsedJson && Array.isArray(parsedJson.data) ? parsedJson.data : [];

  const meta: RespAPI["meta"] | null =
    parsedJson && parsedJson.meta ? parsedJson.meta : null;

  return {
    rows: flattenToRows(dataRaw),
    meta,
    resp: parsedJson,
  };
}

/** ---------------------- Hook genérico ---------------------- */

function useEntradasGenerico(
  moduloAtual: ModuloAtual | null,
  authLoading: boolean,
  query?: EntradasQuery
) {
  const [rows, setRows] = useState<EntradaRowUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState<RespAPI["meta"] | null>(null);
  const [resp, setResp] = useState<RespAPI | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const stop = useRef(false);
  const fetching = useRef(false);

  async function carregar() {
    fetching.current = true;

    try {
      setErro(null);

      const result = await fetchEntradasOnce(moduloAtual, query);

      setRows(result.rows);
      setMeta(result.meta);
      setResp(result.resp);
    } catch (e: any) {
      if (e?.name !== "AbortError") {
        setErro(e?.message || "Erro ao carregar entradas.");
        throw e;
      }
    } finally {
      fetching.current = false;
    }
  }

  useEffect(() => {
    stop.current = false;

    setLoading(true);
    setErro(null);
    setRows([]);
    setMeta(null);
    setResp(null);

    if (authLoading) {
      return () => {
        stop.current = true;
      };
    }

    if (!moduloAtual?.id) {
      setLoading(false);
      setErro("Módulo de estoque não identificado para esta rota.");
      return () => {
        stop.current = true;
      };
    }

    (async () => {
      try {
        await carregar();
        setLoading(false);

        while (!stop.current) {
          while (fetching.current && !stop.current) {
            await sleep(25);
          }

          if (stop.current) break;

          await sleep(jitter(POLL_FALLBACK_MS));

          if (stop.current) break;

          await carregar();
        }
      } catch (e) {
        if (!stop.current) {
          console.error("useEntradasGenerico loop error:", e);
        }

        setLoading(false);
      }
    })();

    return () => {
      stop.current = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduloAtual?.id, authLoading, JSON.stringify(query)]);

  const refresh = async () => {
    while (fetching.current) {
      await sleep(25);
    }

    await carregar();
  };

  return {
    rows,
    loading,
    refresh,
    meta,
    resp,
    erro,
  };
}

/** ---------------------- Página ---------------------- */

const EntradasGenerico: React.FC = () => {
  const { areaSlug, estoqueSlug } = useParams<{
    areaSlug: string;
    estoqueSlug: string;
  }>();

  const { sessao, loading: authLoading } = useAuth();

  const config = useMemo(
    () => getConfigByParams(areaSlug, estoqueSlug),
    [areaSlug, estoqueSlug]
  );

  const moduloAtual = useMemo(
    () => findModuloAtualFromSessao(sessao, config),
    [sessao, config]
  );

  const headers = useMemo(
    () => [
      "Responsável",
      "Código",
      "Nome do Produto",
      "Custo Unitário",
      "Quantidade",
      "Posição",
      "Moeda",
      "Data",
      "Hora",
    ],
    []
  );

  const columnWidths = useMemo(
    () => [
      "160px",
      "90px",
      "360px",
      "120px",
      "90px",
      "80px",
      "70px",
      "100px",
      "80px",
    ],
    []
  );

  const enabledColumnIndexes = useMemo(
    () => headers.map((_header, index) => index),
    [headers]
  );

  const { rows, loading, erro, resp } = useEntradasGenerico(
    moduloAtual,
    authLoading
  );

  const pageLoading = authLoading || loading;

  const title = useMemo(() => {
    const estoqueNome = resp?.estoque?.nome;
    const moduloLabel = resp?.modulo?.label || moduloAtual?.label;

    if (estoqueNome)
      return `${slugToTitle(config.areaSlug)} - Entradas - ${estoqueNome}`;
    if (moduloLabel)
      return `${slugToTitle(config.areaSlug)} - Entradas - ${moduloLabel}`;

    return config.title;
  }, [config.areaSlug, config.title, resp, moduloAtual]);

  const reportTitle = useMemo(() => {
    const estoqueNome = resp?.estoque?.nome;
    if (estoqueNome) return `Relatório de Entradas Estoque - ${estoqueNome}`;
    return config.reportTitle;
  }, [config.reportTitle, resp]);

  const totalProdutos = useMemo(() => {
    return rows.reduce((acc, row) => {
      const quantidade = parseInt(String(row[4] ?? "0"), 10);
      return acc + (Number.isFinite(quantidade) ? quantidade : 0);
    }, 0);
  }, [rows]);

  const totaisPorMoeda = useMemo(() => {
    const acc: Record<string, number> = {};

    rows.forEach((row) => {
      const custo = parseCurrency(row[3]);
      const quantidade = parseInt(String(row[4] ?? "0"), 10);
      const moeda = String(row[6] || "BRL").toUpperCase();

      const incremento =
        Number.isFinite(custo) && Number.isFinite(quantidade)
          ? custo * quantidade
          : 0;

      if (!acc[moeda]) acc[moeda] = 0;
      acc[moeda] += incremento;
    });

    return acc;
  }, [rows]);

  const footerLines = useMemo(() => {
    const lines: string[] = [];

    lines.push(`Total de Produtos Adicionados no Estoque: ${totalProdutos}`);

    const entries = Object.entries(totaisPorMoeda).sort(([a], [b]) =>
      a.localeCompare(b)
    );

    entries.forEach(([moeda, valor]) => {
      const valorFormatado = formatMoney(valor, moeda);

      if (moeda === "BRL") {
        lines.push(
          `Valor Total de Produtos Adicionados no Estoque (${moeda}): ${valorFormatado} (${numeroParaExtenso(
            Math.floor(valor)
          )} reais)`
        );
        return;
      }

      lines.push(
        `Valor Total de Produtos Adicionados no Estoque (${moeda}): ${valorFormatado}`
      );
    });

    return lines;
  }, [totalProdutos, totaisPorMoeda]);

  const gerarRelatorioEntradas = useCallback(() => {
    generatePdfReport({
      reportTitle,
      fileNamePrefix: config.fileNamePrefix,
      subtitle: "Informações dos Produtos Adicionados no Estoque",
      headers,
      rows,
      custoColIndex: 3,
      enabledColumns: enabledColumnIndexes,
      footerLines,
    });
  }, [
    reportTitle,
    config.fileNamePrefix,
    headers,
    rows,
    enabledColumnIndexes,
    footerLines,
  ]);

  useEffect(() => {
    const handler = (event: Event) => {
      const custom = event as CustomEvent<{
        actionKey?: string;
        label?: string;
        codigo?: string | null;
        departamento?: {
          id?: number | string | null;
          name?: string;
          slug?: string | null;
          routeKey?: string;
        };
        modulo?: {
          id?: number | string | null;
          label?: string;
          codigo?: string | null;
          slug?: string;
        };
        pathContext?: {
          pathname?: string;
          selectedAddressValue?: string;
        };
      }>;

      if (custom.detail?.actionKey !== "generate_report") return;

      const currentPath = normalizeSlug(window.location.pathname);
      const eventPath = normalizeSlug(
        custom.detail?.pathContext?.pathname || ""
      );

      const moduloLabel = normalizeSlug(custom.detail?.modulo?.label || "");
      const moduloCodigo = normalizeSlug(custom.detail?.modulo?.codigo || "");
      const moduloSlug = normalizeSlug(custom.detail?.modulo?.slug || "");

      const base = normalizeSlug(
        `/${config.areaSlug}/${config.estoqueSlug}/entradas`
      );

      const isEntradasContext =
        currentPath.includes(base) ||
        eventPath.includes(base) ||
        currentPath.includes("entradas") ||
        eventPath.includes("entradas") ||
        moduloLabel.includes("entradas") ||
        moduloCodigo.includes("entradas") ||
        moduloSlug.includes("entradas");

      if (!isEntradasContext) return;

      gerarRelatorioEntradas();
    };

    window.addEventListener("logvi:menu-action", handler);

    return () => {
      window.removeEventListener("logvi:menu-action", handler);
    };
  }, [config.areaSlug, config.estoqueSlug, gerarRelatorioEntradas]);

  return (
    <div>
      <TitleButtons fullBleedLine title={title} />

      {erro && (
        <div
          style={{
            marginBottom: 12,
            padding: "10px 12px",
            border: "1px solid #efb4b4",
            background: "#fff1f1",
            color: "#7f1d1d",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {erro}
        </div>
      )}

      {pageLoading ? (
        <div style={{ padding: 16 }}>Carregando entradas…</div>
      ) : (
        <Table
          headers={headers}
          rows={rows}
          columnWidths={columnWidths}
          responsiveWidths={{
            1300: {
              1: "140px",
              2: "80px",
              3: "320px",
              4: "110px",
              5: "80px",
              6: "70px",
              7: "60px",
              8: "100px",
              9: "70px",
            },
          }}
        />
      )}
    </div>
  );
};

export default EntradasGenerico;
